export interface PrivacyLink {
    label: string;
    href: string;
}

export interface PrivacyGroup {
    heading: string;
    content?: string[];
    bullets?: string[];
    footer?: string;

    link?: PrivacyLink;
    footerLink?: PrivacyLink;
}

export interface PolicySection {
    id: string;
    number: string;
    title: string;

    content?: string[];
    bullets?: string[];
    bullets2?: string[];

    subHeading?: string;
    footer?: string;

    groups?: PrivacyGroup[];

    contact?: {
        organization: string;
        legal: string;
        support: string;
        website: string;
    };

    agreement?: {
        title: string;
        description: string;
    };
}

export const privacyData: PolicySection[] = [
    {
        id: "information-we-collect",
        number: "1",
        title: "Information we collect",

        content: [
            "We collect the following types of information."
        ],

        groups: [
            {
                heading: "A · INFORMATION YOU PROVIDE",
            },
            {
                heading: "Account information",
                bullets: [
                    "Name, email address, phone number, and mailing address.",
                    "Professional licenses and certifications.",
                    "Business name and tax identification number.",
                    "Payout and banking details — handled securely by our payment processor, Stripe (via Stripe Connect). Tullu does not store your full bank account or card numbers.",
                    "Professional liability insurance details."
                ]
            },
            {
                heading: "Profile information",
                bullets: [
                    "Profile photos and portfolio images.",
                    "Service descriptions and pricing.",
                    "Availability and scheduling preferences.",
                    "Professional bio and qualifications."
                ]
            },
            {
                heading: "Client information",
                bullets: [
                    "Client names and contact information.",
                    "Intake forms and consent documents.",
                    "Treatment notes and service records.",
                    "Photos (before/after, treatment documentation).",
                    "Communications with clients through the Platform."
                ]
            },
            {
                heading: "B · INFORMATION COLLECTED AUTOMATICALLY",
                bullets: [
                    "Device information (device type, operating system, unique device identifiers).",
                    "Log information (IP address, browser type, access times, pages viewed).",
                    "Location information (if you grant permission).",
                    "Usage data (features used, actions taken, time spent in the app).",
                    "Cookies and similar tracking technologies."
                ]
            },
            {
                heading: "C · INFORMATION FROM THIRD PARTIES",
                bullets: [
                    "Payment processor (transaction details and payout status from Stripe).",
                    "Identity verification services (credential verification).",
                    "Background check providers (if applicable).",
                    "SMS service providers (message delivery status)."
                ]
            },
        ]
    },
    {
        id: "how-we-use-information",
        number: "2",
        title: "How we use your information",

        content: [
            "We use your information to:"
        ],

        groups: [
            {
                heading: "PROVIDE & IMPROVE SERVICES",
                bullets: [
                    "Create and manage your provider account.",
                    "Facilitate bookings and connections with clients.",
                    "Process payments and manage transactions.",
                    "Provide customer support and respond to inquiries.",
                    "Send appointment reminders and notifications.",
                    "Improve and optimize the Platform."
                ]
            },
            {
                heading: "COMMUNICATION",
                bullets: [
                    "Send transactional messages (booking confirmations, payment receipts).",
                    "Send service updates and Platform notifications.",
                    "Respond to your requests and questions.",
                    "Send marketing communications (with your consent).",
                    "Conduct surveys and gather feedback."
                ]
            },
            {
                heading: "SAFETY & SECURITY",
                bullets: [
                    "Verify your identity and credentials.",
                    "Detect and prevent fraud, abuse, and illegal activity.",
                    "Protect the security and integrity of the Platform.",
                    "Enforce our Terms of Service.",
                    "Comply with legal obligations."
                ]
            },
            {
                heading: "LEGAL & COMPLIANCE",
                bullets: [
                    "Comply with applicable laws and regulations.",
                    "Respond to legal requests and prevent harm.",
                    "Generate tax documents (e.g., IRS Form 1099).",
                    "Maintain records as required by law."
                ]
            }
        ]
    },
    {
        id: "how-we-share-information",
        number: "3",
        title: "How we share your information",

        content: [
            "We may share your information in the following circumstances."
        ],

        groups: [
            {
                heading: "WITH CLIENTS",
                content: [
                    "Your profile information (name, photo, bio, services, pricing, availability) is visible to clients using the Platform. Clients you accept bookings from may see your contact information."
                ]
            },
            {
                heading: "WITH SERVICE PROVIDERS",
                content: [
                    "We share information with third-party service providers who help us operate the Platform, including:"
                ],
                bullets: [
                    "Payment processing (Stripe).",
                    "Cloud hosting providers.",
                    "SMS and email service providers.",
                    "Analytics providers.",
                    "Customer support tools."
                ],
                footer:
                    "These providers are contractually obligated to protect your information and use it only for the purposes we specify."
            },
            {
                heading: "FOR LEGAL REASONS",
                content: [
                    "We may disclose information if required by law or in response to:"
                ],
                bullets: [
                    "Legal processes (subpoenas, court orders, government requests).",
                    "Enforcement of our Terms of Service.",
                    "Protection of our rights, property, or safety.",
                    "Investigation of fraud or security issues."
                ]
            },
            {
                heading: "BUSINESS TRANSFERS",
                content: [
                    "If Tullu is involved in a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity."
                ]
            },
            {
                heading: "WITH YOUR CONSENT",
                content: [
                    "We may share information with third parties when you explicitly consent or direct us to do so."
                ]
            },
            {
                heading: "AGGREGATED OR DE-IDENTIFIED DATA",
                content: [
                    "We may share aggregated or de-identified information that cannot reasonably be used to identify you."
                ]
            }
        ]
    },
    {
        id: "hipaa-protected-health-information",
        number: "4",
        title: "HIPAA and protected health information",

        content: [
            "If you are a covered entity under HIPAA, Tullu acts as your Business Associate with respect to Protected Health Information (PHI) stored in the Platform."
        ],

        groups: [
            {
                heading: "Our obligations as a Business Associate include",
                bullets: [
                    "Using and disclosing PHI only as permitted by the Business Associate Agreement.",
                    "Implementing appropriate safeguards to protect PHI.",
                    "Reporting breaches of unsecured PHI.",
                    "Making PHI available for access and amendment as required.",
                    "Returning or destroying PHI upon termination."
                ]
            }
        ],

        footer:
            "You remain responsible for obtaining proper patient authorizations, maintaining HIPAA compliance in your practice, and protecting PHI in your possession."
    },
    {
        id: "your-choices-and-rights",
        number: "5",
        title: "Your choices and rights",

        groups: [
            {
                heading: "ACCOUNT INFORMATION",
                content: [
                    "You may update your account information at any time through the Partner App settings."
                ]
            },
            {
                heading: "MARKETING COMMUNICATIONS",
                content: [
                    'You may opt out of marketing emails by clicking "unsubscribe" in any marketing message or updating your communication preferences in the app. You cannot opt out of transactional messages (booking confirmations, payment receipts, and similar).'
                ]
            },
            {
                heading: "SMS MESSAGES",
                content: [
                    "You may opt out of SMS messages by replying STOP to any SMS message or updating your settings in the app."
                ],
                link: {
                    label: "SMS Consent",
                    href: "/sms-consent"
                }
            },
            {
                heading: "LOCATION INFORMATION",
                content: [
                    "You can control location permissions through your device settings."
                ]
            },
            {
                heading: "COOKIES",
                content: [
                    "You can control cookies through your browser settings, though this may limit Platform functionality."
                ]
            },
            {
                heading: "ACCESS AND DELETION",
                content: [
                    "You have the right to:"
                ],
                bullets: [
                    "Access the personal information we hold about you.",
                    "Request correction of inaccurate information.",
                    "Request deletion of your information (subject to legal retention requirements).",
                    "Export your data in a portable format."
                ],
                footer:
                    "To exercise these rights, contact us at",
                footerLink: {
                    label: "privacy@tulluapp.com",
                    href: "mailto:privacy@tulluapp.com"
                }
            }
        ]
    },
    {
        id: "data-security",
        number: "6",
        title: "Data security",

        content: [
            "We implement appropriate technical and organizational measures to protect your information, including:"
        ],

        bullets: [
            "Encryption of data in transit (TLS/SSL) and at rest.",
            "Regular security assessments and audits.",
            "Access controls and authentication.",
            "Employee training on data protection.",
            "Incident response and breach notification procedures."
        ],

        footer:
            "However, no method of transmission or storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security."
    },
    {
        id: "data-retention",
        number: "7",
        title: "Data retention",

        content: [
            "We retain your information for as long as necessary to:"
        ],

        bullets: [
            "Provide the Platform and fulfill the purposes described in this policy.",
            "Comply with legal obligations (e.g., tax records, regulatory requirements).",
            "Resolve disputes and enforce our agreements.",
            "Protect against fraudulent or illegal activity."
        ],

        footer:
            "Upon account termination, we will delete or de-identify your information within a reasonable timeframe, except where retention is required by law or legitimate business purposes."
    },
    {
        id: "childrens-privacy",
        number: "8",
        title: "Children's privacy",

        groups: [
            {
                heading: "",
                content: [
                    "The Partner App is intended for use by licensed professionals aged 18 and older. We do not knowingly collect information from individuals under 18. If you believe we have collected information from a minor, please contact us immediately at"
                ],
                link: {
                    label: "privacy@tulluapp.com",
                    href: "mailto:privacy@tulluapp.com"
                }
            }
        ]
    },
    {
        id: "third-party-links-and-services",
        number: "9",
        title: "Third-party links and services",

        content: [
            "The Partner App may contain links to third-party websites or integrate with third-party services. We are not responsible for the privacy practices of these third parties. We encourage you to review their privacy policies."
        ]
    },
    {
        id: "international-data-transfers",
        number: "10",
        title: "International data transfers",

        content: [
            "Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. By using the Partner App, you consent to such transfers."
        ]
    },
    {
        id: "state-specific-privacy-rights",
        number: "11",
        title: "State-specific privacy rights",

        groups: [
            {
                heading: "CALIFORNIA RESIDENTS",
                content: [
                    "Under the California Consumer Privacy Act (CCPA), California residents have additional rights, including:"
                ],
                bullets: [
                    "Right to know what personal information is collected and how it is used.",
                    "Right to delete personal information.",
                    "Right to opt out of the sale of personal information (we do not sell personal information).",
                    "Right to non-discrimination for exercising privacy rights."
                ],
                footer: "To exercise these rights, contact us at",
                footerLink: {
                    label: "privacy@tulluapp.com",
                    href: "mailto:privacy@tulluapp.com"
                }
            },
            {
                heading: "OTHER STATES",
                content: [
                    "Residents of Virginia, Colorado, Connecticut, and other states with comprehensive privacy laws may have similar rights. Contact us to exercise these rights."
                ]
            }
        ]
    },
    {
        id: "changes-to-this-privacy-policy",
        number: "12",
        title: "Changes to this Privacy Policy",

        content: [
            "We may update this Privacy Policy from time to time. We will notify you of material changes by:"
        ],

        bullets: [
            "Posting the updated policy in the Partner App.",
            "Sending an email to your registered address.",
            "Displaying a notification when you log in."
        ],

        footer:
            "Your continued use of the Partner App after changes become effective constitutes acceptance of the updated policy."
    },
    {
        id: "contact-us",
        number: "13",
        title: "Contact us",

        content: [
            "If you have questions about this Privacy Policy or wish to exercise your privacy rights, please contact us:"
        ],

        contact: {
            organization: "Tullu Technologies LLC",
            legal: "privacy@tulluapp.com",
            support: "support@tulluapp.com",
            website: "tulluapp.com"
        },


        agreement: {
            title:
                "Tullu does not sell your personal information — yours or your clients'.",
            description:
                "Questions about your data, or a Business Associate Agreement? Email privacy@tulluapp.com."
        }
    },
];